import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { WhatIfDialogComponent, WhatIfDialogData } from '../../temporal/whatif-dialog.component';
import { GraphComponent, IEdge } from '@yfiles/yfiles';
import { Subscription } from 'rxjs';
import { AppComponent } from '../../app.component';
import { ANALYSIS_URL } from '../../app-urls';
import { AnalysisEdge, AnalysisNode } from '../../common/analysis-model';
import { CURRENT_STATE_PROJECT_KEY, GraphData, Linqset, NODE_SUPERTYPE, NODE_TYPE, ORGANISATION_MODEL_KEY } from '../../common/linqset-model';
import { createLinqset } from '../../common/model-processing';
import { createInsightsFilter, DEFAULT_CURRENCY, defaultCapacityHours, getCurrency, Organisation } from '../../common/organisation';
import { LENSES, LensKey, modelColours, MODEL_COLOURS } from '../../lenses/lens';
import { AnalysisScope, modelsInScope, ScopeService } from '../../analysis/scope';
import { UserService } from '../../user/user.service';
import { LayoutOrientationStringValues } from '@yfiles/yfiles';
import { LensGraphComponent } from '../../lenses/lens-graph.component';
import { LensGraphEdge, LensGraphNode, LensNodeTag, STATUS_COLOURS } from '../../lenses/lens-node-style';
import { LensSimulation, LensSimulationProgress } from '../../lenses/lens-simulation';
import { buildRolesLens, HOURS_RAMP, RoleLens, RoleRun, RolesColourBy, rolesLensGraph } from '../../lenses/roles-lens';
import { Handover, LensOutput, LensRun } from '../../lenses/runs';
import { FullModelComponent } from '../../analysis/full-model.component';
import { GraphSearchNode } from '../../ui-components/search/search.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
    AUTOMATION_COLOURS, buildSystemsLens, INTERFACE_COLOURS, INTERFACE_LABELS, InterfaceKind, RUN_AUTOMATION_LABELS, RunAutomation, SystemHandover, SystemLens,
    SystemRun, SystemsColourBy, systemsLensGraph, SYSTEMS_RAMP,
} from '../../lenses/systems-lens';
import { FormattingService } from '../../linq-utils/formatting/formatting.service';
import { MODEL_API, RepositoryService } from '../../linq-utils/repository.service';
import { FUNCTION, FunctionService } from '../../model-services/function.service';
import { HelpService } from '../../help/help-services';
import { analysisTour } from '../../analysis/analysis-tour';
import { InsightsService } from '../../model-services/insights.service';
import { OrganisationService } from '../../organisation/organisation.service';
import { AUTOMATION_LABELS, AutomationLevel, buildSystemsReport, externalRoleKeys, SystemsReport } from '../../systems/systems-model';
import { buildWorkforceReport, PersonSummary, WORKFORCE_STATUS_LABELS, WorkforceReport, WorkforceStatus } from '../../workforce/workforce-model';
import { SCREEN } from '../layouts-enums';

const COMPONENT = 'LensAnalysisLayoutComponent';

type ColourBy = RolesColourBy | SystemsColourBy;

// what each lens offers to colour its nodes by, and the report beside it
const COLOUR_OPTIONS: Record<Exclude<LensKey, 'actions' | 'overview' | 'insights' | 'tags' | 'information' | 'nodes' | 'ai'>, { value: ColourBy; label: string }[]> = {
    people: [{ value: 'status', label: 'Capacity' }, { value: 'hours', label: 'Hours' }, { value: 'none', label: 'None' }],
    systems: [{ value: 'automation', label: 'Automation' }, { value: 'cost', label: 'Cost' }, { value: 'carbon', label: 'Carbon' }, { value: 'none', label: 'None' }],
};

// The analysis screen seen through a lens other than actions: the same models, drawn from another point of view. Each
// node is a performer's run of consecutive actions (a role's on the People & roles lens, a system's on the Systems lens)
// and the edges are the hand-overs between them, coloured by the models the work is in, with the action simulation
// rolled up to the runs and the lens's report beside it
@Component({
    selector: 'app-lens-analysis-layout',
    templateUrl: './lens-analysis-layout.html',
    styleUrls: ['./lens-analysis-layout.css'],
    standalone: false,
})
export class LensAnalysisLayoutComponent implements OnInit, OnDestroy {
    readonly screen = SCREEN.ANALYSIS;
    readonly statusColours = STATUS_COLOURS;
    readonly hoursRamp = HOURS_RAMP;
    readonly systemsRamp = SYSTEMS_RAMP;
    readonly legendStatuses: WorkforceStatus[] = ['over', 'stretched', 'healthy', 'under', 'unknown'];
    readonly legendAutomation: RunAutomation[] = ['automated', 'mixed', 'assisted', 'manual'];
    readonly legendInterfaces: InterfaceKind[] = ['automated', 'mixed', 'manual'];
    readonly automationColours = AUTOMATION_COLOURS;
    readonly interfaceColours = INTERFACE_COLOURS;

    lensKey: Exclude<LensKey, 'actions' | 'overview' | 'insights' | 'tags' | 'information' | 'nodes' | 'ai'> = 'people';
    organisationKey = '';
    organisation?: Organisation;
    linqset?: Linqset;
    workspaceKey = CURRENT_STATE_PROJECT_KEY;
    loading = true;
    error = '';
    fullModel = false;                  // the LINQ model itself is shown in place of the graph

    workforce?: WorkforceReport;
    systemsReport?: SystemsReport;
    // in a project, the current state's organisation model and work, and the reports for it the project's are compared with
    private currentState?: { organisationModel: GraphData; analysis: { nodes: AnalysisNode[]; edges: AnalysisEdge[] } };
    baselineWorkforce?: WorkforceReport;
    baselineSystems?: SystemsReport;
    models: { key: string; name: string; colour: string }[] = [];
    selectedModels: string[] = [];
    colourBy: ColourBy = 'status';

    runs: LensRun[] = [];
    handovers: Handover[] = [];
    outputs: LensOutput[] = [];
    outputLinks: Handover[] = [];
    graphNodes: LensGraphNode[] = [];
    graphEdges: LensGraphEdge[] = [];
    selected?: LensRun;

    simulation?: LensSimulation;
    progress?: LensSimulationProgress;
    waiting: { run: LensRun; queue: number }[] = [];

    @ViewChild(LensGraphComponent) lensGraph?: LensGraphComponent;
    @ViewChild(FullModelComponent) private fullModelView?: FullModelComponent;
    // the cards, the outputs and the actions in each card, for search to find
    searchNodes: GraphSearchNode[] = [];
    @ViewChild('rightdrawer') rightDrawer?: MatSidenav;

    private analysis: { nodes: AnalysisNode[]; edges: AnalysisEdge[] } = { nodes: [], edges: [] };
    private names = new Map<string, string>();
    private organisationModel?: GraphData;
    private aiAgents = new Set<string>();
    private colours = new Map<string, string>();
    private roleLens?: RoleLens;
    private systemLens?: SystemLens;
    private graphComponent?: GraphComponent;
    private subs: Subscription[] = [];
    private request = 0;
    private scope?: AnalysisScope;
    private focusKey?: string;          // a node to show once the graph is built, from a link

    constructor(private route: ActivatedRoute, private router: Router, private organisationService: OrganisationService, private repoService: RepositoryService,
        private insightsService: InsightsService, private functionService: FunctionService, private formatter: FormattingService, private scopeService: ScopeService,
        private userService: UserService, private dialog: MatDialog, private snackbar: MatSnackBar,
        private helpService: HelpService) {}

    // what if the load or the people on a role changed: the roles' needs and a simulation against today, in the browser
    openWhatIf() {
        if (!this.organisation || !this.organisationModel) return;
        const currency = getCurrency(this.organisation.currencyCode || DEFAULT_CURRENCY);
        const data: WhatIfDialogData = {
            organisation: this.organisation,
            analysis: this.analysis,
            modelKeys: [...this.selectedModels],
            organisationModel: this.organisationModel,
            workspaceKey: this.workspaceKey,
            formatCost: (value) => this.formatter.formatAbbreviatedNumber(value, currency),
        };
        this.dialog.open<WhatIfDialogComponent, WhatIfDialogData>(WhatIfDialogComponent, { data, width: '860px', maxWidth: '95vw', autoFocus: false });
    }

    // the graph lays out in the direction the user configured for the model canvas
    get orientation(): LayoutOrientationStringValues {
        return (this.userService.getUserPreferences()?.graphDirection as LayoutOrientationStringValues) || 'top-to-bottom';
    }

    ngOnInit() {
        this.subs.push(this.route.params.subscribe(p => {
            const lens = LENSES.find(l => l.key === p['lens']);
            // the actions lens and the overview have their own screens; anything unknown goes to the actions
            if (!lens || lens.key === 'actions' || lens.key === 'overview' || lens.key === 'insights' || lens.key === 'tags' || lens.key === 'information' || lens.key === 'nodes' || lens.key === 'ai') {
                void this.router.navigateByUrl(ANALYSIS_URL(p['organisation_key']));
                return;
            }
            this.lensKey = lens.key;
            this.colourBy = COLOUR_OPTIONS[lens.key][0].value;
            this.selected = undefined;
            this.organisationKey = p['organisation_key'];
            this.focusKey = this.route.snapshot.queryParams['focus'];
            this.scope = undefined;
            this.scopeService.open(this.organisationKey);
            void this.load();
        }));
        // the scope changed on the header: new models are a new graph; a new workspace is new data
        this.subs.push(this.scopeService.scope$.subscribe(scope => {
            const previous = this.scope;
            this.scope = scope;
            if (!previous || !this.organisation) return;
            if (scope.workspaceKey !== previous.workspaceKey) void this.load();
            else this.applyScope();
        }));
    }

    ngOnDestroy() {
        this.subs.forEach(s => s.unsubscribe());
        this.simulation?.clear();
        this.helpService.endTour();
    }

    // the models the scope shows, of those with work in them
    private applyScope() {
        if (!this.organisation || !this.scope) return;
        const inScope = new Set(modelsInScope(this.scope, this.organisation).map(m => m._key));
        this.selectedModels = this.models.filter(m => inScope.has(m.key)).map(m => m.key);
        this.buildLens();
    }

    // what search finds here: each card (a role's or system's run of work), each output, and each action, which leads
    // to the card that holds it
    private buildSearchNodes(): GraphSearchNode[] {
        const actionNames = new Map(this.analysis.nodes.filter(n => n.superType === NODE_SUPERTYPE.ACTION).map(n => [n._key, n.name]));
        const kind = (run: LensRun) => this.lensKey === 'people' ? ((run as RoleRun).automated ? 'Automated work' : 'Role') : ((run as SystemRun).systemKeys?.length ? 'System' : 'Work outside systems');
        // a role or system can have several cards, one for each run of its work: the first action tells them apart
        const cards = this.runs.map(r => {
            const first = actionNames.get(r.actionKeys[0])
            const count = r.actionKeys.length > 1 ? ` and ${r.actionKeys.length - 1} more` : ''
            return { key: r.key, name: r.name, description: `${kind(r)} · ${first ? first + count : r.actionKeys.length + ' actions'}` }
        });
        const outputs = this.outputs.map(o => ({ key: o.key, name: o.name, description: 'Output', type: NODE_TYPE.INFORMATION }));
        const actions = this.runs.flatMap(r => r.actionKeys.map(k => ({ key: k, name: actionNames.get(k) || '', description: `Action · in ${r.name}`, type: NODE_TYPE.ACTION })));
        return [...cards, ...outputs, ...actions].filter(n => n.name);
    }

    // a node found with search: its card, or the output, shown and selected; in the full model, the action itself
    findNode(key: string) {
        const run = this.runs.find(r => r.key === key) || this.runs.find(r => r.actionKeys.includes(key));
        const output = this.outputs.find(o => o.key === key);
        const name = this.searchNodes.find(n => n.key === key)?.name || 'That node';
        if (this.fullModel) {
            const target = output ? output.infoKey : run && run.key === key ? run.actionKeys[0] : key;
            if (!this.fullModelView?.trace(target)) this.snackbar.open(`${name} isn't in the model`, 'close', { duration: 4000 });
            return;
        }
        if (run) this.focus(run);
        else if (output) void this.lensGraph?.focus(output.key);
        else this.snackbar.open(`${name} isn't in the graph`, 'close', { duration: 4000 });
    }

    // a node from a link: the run that holds the action, or that the role or system performs
    private showFocus() {
        const key = this.focusKey;
        if (!key) return;
        this.focusKey = undefined;
        const run = this.runs.find(r => r.actionKeys.includes(key))
            || this.runs.find(r => ((r as RoleRun).roleKeys || (r as SystemRun).systemKeys || []).includes(key));
        if (run) this.selected = run;
    }

    get currentScope() {
        return this.scope;
    }

    get lensLabel() {
        return LENSES.find(l => l.key === this.lensKey)?.label || '';
    }

    get colourOptions() {
        return COLOUR_OPTIONS[this.lensKey];
    }

    get hasReport() {
        return this.lensKey === 'people' ? !!this.workforce : !!this.systemsReport;
    }

    private async load() {
        const request = ++this.request;
        this.loading = true;
        this.error = '';
        this.clearSimulation();
        this.workspaceKey = this.scope?.workspaceKey || this.scopeService.scope.workspaceKey;
        try {
            const [organisation, organisationModel] = await Promise.all([
                this.organisationService.checkOrganisationDataForRoute(this.organisationKey),
                this.insightsService.afterRefresh(this.workspaceKey, () => this.repoService.callSingleAPI(this.repoService.getData<GraphData>(MODEL_API(this.organisationKey, ORGANISATION_MODEL_KEY, this.workspaceKey)),
                    COMPONENT, 'Load the organisation model')),
            ]);
            if (request !== this.request) return;
            this.organisation = organisation;
            this.linqset = createLinqset(organisation, createInsightsFilter());
            const [analysis, currentState] = await Promise.all([
                this.insightsService.loadAnalysisGraphForFilter(organisation, createInsightsFilter(this.workspaceKey)) as Promise<{ nodes: AnalysisNode[]; edges: AnalysisEdge[] }>,
                this.loadCurrentState(),
            ]);
            if (request !== this.request) return;
            this.analysis = analysis;
            this.currentState = currentState;

            this.organisationModel = organisationModel;
            this.names = new Map(organisationModel.nodes.map(n => [n._key, n.name || 'Unnamed']));
            this.aiAgents = new Set(organisationModel.nodes.filter(n => n.superType === NODE_SUPERTYPE.SYSTEM && n.type === NODE_TYPE.AI_AGENT).map(n => n._key));

            // the models with work in them, each in the colour it has in every lens
            this.colours = modelColours(organisation);
            const withWork = new Set(this.analysis.nodes.filter(n => n.superType === NODE_SUPERTYPE.ACTION).map(n => n.modelKey));
            this.models = organisation.models.filter(m => withWork.has(m._key))
                .map((m, i) => ({ key: m._key, name: m.name, colour: this.colours.get(m._key) || MODEL_COLOURS[i % MODEL_COLOURS.length] }));
            this.models.forEach(m => this.colours.set(m.key, m.colour));
            this.scope = this.scope || this.scopeService.scope;
            this.applyScope();
        } catch (err) {
            if (request !== this.request) return;
            console.error('Unable to load the analysis', err);
            this.error = "The analysis couldn't be loaded. Please try again.";
        } finally {
            if (request === this.request) this.loading = false;
        }
    }

    // in a project, the current state's organisation model and work, for the report's figures to be compared with. The
    // project is still shown without them if they can't be loaded
    private async loadCurrentState(): Promise<typeof this.currentState> {
        if (this.workspaceKey === CURRENT_STATE_PROJECT_KEY) return undefined;
        try {
            const [organisationModel, analysis] = await Promise.all([
                this.repoService.callSingleAPI(this.repoService.getData<GraphData>(MODEL_API(this.organisationKey, ORGANISATION_MODEL_KEY, CURRENT_STATE_PROJECT_KEY)), COMPONENT, 'Load the current state organisation model'),
                this.insightsService.loadAnalysisGraphForFilter(this.organisation!, createInsightsFilter(CURRENT_STATE_PROJECT_KEY)) as Promise<{ nodes: AnalysisNode[]; edges: AnalysisEdge[] }>,
            ]);
            return { organisationModel, analysis };
        } catch (err) {
            console.error('Unable to load the current state to compare with', err);
            return undefined;
        }
    }

    // a new graph for the models chosen; a simulation over the old one can't carry on
    private buildLens() {
        this.clearSimulation();
        const shown = new Set(this.selectedModels);
        this.buildReport(shown);
        if (this.lensKey === 'people') {
            if (!this.workforce) return;
            this.roleLens = buildRolesLens(this.analysis, this.workforce, this.names, shown);
            this.systemLens = undefined;
            this.runs = this.roleLens.runs;
            this.handovers = this.roleLens.handovers;
            this.outputs = this.roleLens.outputs;
            this.outputLinks = this.roleLens.outputLinks;
        } else {
            this.systemLens = buildSystemsLens(this.analysis, this.names, this.aiAgents, shown, externalRoleKeys(this.organisationModel!));
            this.roleLens = undefined;
            this.runs = this.systemLens.runs;
            this.handovers = this.systemLens.handovers;
            this.outputs = this.systemLens.outputs;
            this.outputLinks = this.systemLens.outputLinks;
        }
        const { nodes, edges } = this.graph();
        this.graphNodes = nodes;
        this.graphEdges = edges;
        this.searchNodes = this.buildSearchNodes();
        if (this.selected) this.selected = this.runs.find(r => r.key === this.selected!.key);
        this.showFocus();
    }

    // the lens's report, for the models shown: the whole organisation's when every model is, else just theirs
    private buildReport(shown: Set<string>) {
        const organisation = this.organisation, organisationModel = this.organisationModel;
        if (!organisation || !organisationModel) return;
        const currency = getCurrency(organisation.currencyCode || DEFAULT_CURRENCY);
        const scoped = shown.size < this.models.length;
        const analysis = scoped ? { nodes: this.analysis.nodes.filter(n => shown.has(n.modelKey)), edges: this.analysis.edges.filter(e => shown.has(e.modelKey)) } : this.analysis;
        const reportInput = {
            organisationModel, analysis, workspaceKey: this.workspaceKey,
            modelNames: Object.fromEntries(organisation.models.map(m => [m._key, m.name])),
            money: (v: number) => this.formatter.formatAbbreviatedNumber(v, currency),
            number: (v: number) => this.formatter.formatAbbreviatedNumber(v),
        };
        this.workforce = this.lensKey === 'people' ? buildWorkforceReport({ ...reportInput, defaultCapacityHours: defaultCapacityHours(organisation), scoped }) : undefined;
        this.systemsReport = this.lensKey === 'systems' ? buildSystemsReport({ ...reportInput, scoped }) : undefined;

        // the same report for the current state, over the same models, to compare with
        const current = this.currentState;
        const baselineInput = current && { ...reportInput, organisationModel: current.organisationModel, workspaceKey: CURRENT_STATE_PROJECT_KEY,
            analysis: scoped ? { nodes: current.analysis.nodes.filter(n => shown.has(n.modelKey)), edges: current.analysis.edges.filter(e => shown.has(e.modelKey)) } : current.analysis };
        this.baselineWorkforce = baselineInput && this.lensKey === 'people' ? buildWorkforceReport({ ...baselineInput, defaultCapacityHours: defaultCapacityHours(organisation), scoped }) : undefined;
        this.baselineSystems = baselineInput && this.lensKey === 'systems' ? buildSystemsReport({ ...baselineInput, scoped }) : undefined;
    }

    private graph() {
        const format = { hours: (v: number) => this.hours(v), count: (v: number) => this.count(v), money: (v: number) => this.money(v) };
        if (this.roleLens) return rolesLensGraph(this.roleLens, this.colours, this.colourBy as RolesColourBy, format);
        if (this.systemLens) return systemsLensGraph(this.systemLens, this.colours, this.colourBy as SystemsColourBy, format);
        return { nodes: [], edges: [] };
    }

    // recolours the cards in place, so the layout stays as it is
    colourByChanged(colourBy: ColourBy) {
        this.colourBy = colourBy;
        if (!this.graphComponent || !this.runs.length) return;
        const shown = new Map(this.graph().nodes.map(n => [n.key, n]));
        this.graphComponent.graph.nodes.forEach(n => {
            const tag = n.tag as LensNodeTag;
            const node = shown.get(tag.lens.key);
            if (node) tag.lens = { ...tag.lens, fill: node.fill, detail: node.detail };
        });
        this.lensGraph?.refresh();
    }

    graphBuilt(graphComponent: GraphComponent) {
        this.graphComponent = graphComponent;
        if (this.selected) void this.lensGraph?.focus(this.selected.key);
    }

    select(key: string | undefined) {
        this.selected = key ? this.runs.find(r => r.key === key) : undefined;
    }

    focus(run: LensRun) {
        this.selected = run;
        void this.lensGraph?.focus(run.key);
    }

    get role(): RoleRun | undefined {
        return this.lensKey === 'people' ? this.selected as RoleRun : undefined;
    }

    get system(): SystemRun | undefined {
        return this.lensKey === 'systems' ? this.selected as SystemRun : undefined;
    }

    // who fills the selected run's roles, busiest first
    get people(): PersonSummary[] {
        const roles = new Set(this.role?.roleKeys || []);
        return (this.workforce?.people || []).filter(p => p.roles.some(r => roles.has(r.key))).sort((a, b) => (b.utilisation || 0) - (a.utilisation || 0));
    }

    // the selected system's figures across all its work, from the systems report
    get systemSummaries() {
        const keys = new Set(this.system?.systemKeys || []);
        return (this.systemsReport?.systems || []).filter(s => keys.has(s.key));
    }

    get handoversIn(): { run: LensRun; handover: Handover }[] {
        return this.handoversOf(h => h.to === this.selected?.key, h => h.from);
    }

    get handoversOut(): { run: LensRun; handover: Handover }[] {
        return this.handoversOf(h => h.from === this.selected?.key, h => h.to);
    }

    // the outputs the selected run creates
    get creates(): { output: LensOutput; items: number }[] {
        if (!this.selected) return [];
        const outputs = new Map(this.outputs.map(o => [o.key, o]));
        return this.outputLinks.filter(l => l.from === this.selected!.key).map(l => ({ output: outputs.get(l.to)!, items: l.items })).filter(c => !!c.output);
    }

    private handoversOf(include: (h: Handover) => boolean, other: (h: Handover) => string) {
        if (!this.selected) return [];
        const runs = new Map(this.runs.map(r => [r.key, r]));
        return this.handovers.filter(include).map(handover => ({ run: runs.get(other(handover))!, handover })).filter(h => !!h.run);
    }

    interfaceKind(h: Handover) {
        return (h as SystemHandover).kind;
    }

    interfaceLabel(kind: InterfaceKind) {
        return INTERFACE_LABELS[kind];
    }

    automationLabel(a: RunAutomation) {
        return RUN_AUTOMATION_LABELS[a];
    }

    levelLabel(l: AutomationLevel) {
        return AUTOMATION_LABELS[l];
    }

    modelName(key: string) {
        return this.models.find(m => m.key === key)?.name || 'Unnamed model';
    }

    modelColour(key: string) {
        return this.colours.get(key) || '#a9a8a3';
    }

    statusLabel(status: WorkforceStatus) {
        return WORKFORCE_STATUS_LABELS[status];
    }

    percent(value: number | null) {
        return value === null ? '–' : `${Math.round(value * 100)}%`;
    }

    hours(value: number) {
        return this.formatter.formatAbbreviatedNumber(value);
    }

    count(value: number) {
        return this.formatter.formatAbbreviatedNumber(value);
    }

    money(value: number) {
        return this.formatter.formatAbbreviatedNumber(value, getCurrency(this.organisation?.currencyCode || DEFAULT_CURRENCY));
    }

    // the simulation: the actions' simulation run over the models shown, rolled up to the runs and hand-overs
    runSimulation() {
        if (!this.graphComponent || !this.runs.length || !this.lensGraph) return;
        this.simulation?.clear();
        const lensGraph = this.lensGraph;
        const edges = new Map<string, IEdge>(lensGraph.edgesOf().map(e => [(e.tag as { edge: LensGraphEdge }).edge.key, e]));
        this.simulation = new LensSimulation(this.graphComponent, () => this.simulationTicked());
        this.simulation.start(this.analysis, new Set(this.selectedModels), {
            nodes: [...this.runs.map(r => ({ key: r.key, actionKeys: r.actionKeys })), ...this.outputs.map(o => ({ key: o.key, actionKeys: [o.infoKey] }))],
            edges: [...this.handovers, ...this.outputLinks].map(h => ({ key: h.key, actionPairs: h.actionPairs })),
        }, key => lensGraph.node(key), key => edges.get(key));
        this.simulationTicked();
    }

    stopSimulation() {
        this.simulation?.stop();
        this.simulationTicked();
    }

    continueSimulation() {
        this.simulation?.continue();
    }

    clearSimulation() {
        this.simulation?.clear();
        this.simulation = undefined;
        this.progress = undefined;
        this.waiting = [];
    }

    private simulationTicked() {
        if (!this.simulation) return;
        this.progress = this.simulation.progress();
        const runs = new Map(this.runs.map(r => [r.key, r]));
        this.waiting = this.simulation.queues().slice(0, 6).map(q => ({ run: runs.get(q.key)!, queue: q.queue })).filter(w => !!w.run);
    }

    async handleFunctionEvent(event: { function: FUNCTION; value?: any }) {
        if (event.function === FUNCTION.TOGGLE_INSIGHTS) {
            await this.rightDrawer?.toggle();
            return;
        }
        if (event.function === FUNCTION.TOGGLE_TEMPORAL_MODEL) {
            if (this.simulation?.running) this.stopSimulation();
            else this.runSimulation();
            return;
        }
        if (event.function === FUNCTION.SCREEN_TOUR) {
            this.helpService.showWhatsThisOverlay(analysisTour(this.lensKey));
            return;
        }
        try {
            await this.functionService.executeFunction(event.function, event.value, undefined, undefined, this.graphComponent);
        } catch (err) {
            console.error(err);
        }
    }

    isHelpOn() {
        return AppComponent.help;
    }
}
